<template>
    <div>
        <app-layout>
	        <template v-slot:header>
                <Header 
                    :title="$t('unit')"
                    :isNewButton="checkPermission('unit_store')"
                    @new-button-click="add"
                    @filter-div-status="datatable.filterStatus = $event"
                ></Header>
	        </template>
            <template v-slot:header-mobile>
                <HeaderMobile 
                    :title="$t('unit')"
                    :isNewButton="checkPermission('unit_store')"
                    :isColumns="true"
                    @new-button-click="add"
                    @filter-div-status="datatable.filterStatus = $event"
                >
                    <template v-slot:columns>
                        <div class="mb-1" v-for="(column, key) in datatable.columns">
                            <b-form-checkbox
                                :id="'checkbox-'+key"
                                :name="'checkbox-'+key"
                                :value="false"
                                :unchecked-value="true"
                                v-model="column.hidden"
                                v-if="column.field!='buttons'"
                            >
                                {{ column.label }}
                            </b-form-checkbox>
                        </div>
                    </template>
                </HeaderMobile>
            </template>
            <datatable-filter v-show="datatable.filterStatus" @filter="filter" @filterClear="filterClear">
                <b-row>
                    <b-col sm="6" md="4" lg="3" xl="2">
                        <b-form-group :label="$t('name')">
                            <b-form-input v-model="datatable.queryParams.filter.name"></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>
            <datatable 
                :isLoading.sync="datatable.isLoading" 
                :columns="datatable.columns" 
                :rows="datatable.rows"
                :total="datatable.total" 
                :queryParams="datatable.queryParams" 
                :lineNumbers="false"
                @on-page-change="onPageChange" 
                @on-sort-change="onSortChange" 
                @on-per-page-change="onPerPageChange"
                v-show="datatable.showTable"
            ></datatable>
            <CommonModal ref="modal" size="md">
                <template v-slot:CommonModalTitle>
                    <div>{{ $t(id == 0 ? 'add' : 'edit') }}</div>
                </template>
                <template v-slot:CommonModalContent>
                    <div class="row mb-5 align-items-center">
                        <div class="col-12">
                            <ValidationObserver ref="storeForm">
                                <div class="row">
                                    <div class="col-12">
                                        <ValidationProvider name="name" rules="required" v-slot="{ valid, errors }">
                                            <b-form-group :label="$t('name')">
                                                <b-form-input 
                                                    v-model="form.name" :state="errors[0] ? false : null"
                                                ></b-form-input>
                                                <b-form-invalid-feedback 
                                                    v-if="errors[0]" v-html="errors[0]"
                                                ></b-form-invalid-feedback>
                                            </b-form-group>
                                        </ValidationProvider>
                                    </div>
                                    <div class="col-12">
                                        <ValidationProvider name="name_en" rules="required" v-slot="{ valid, errors }">
                                            <b-form-group :label="$t('name_en')">
                                                <b-form-input 
                                                    v-model="form.name_en" :state="errors[0] ? false : null"
                                                ></b-form-input>
                                                <b-form-invalid-feedback 
                                                    v-if="errors[0]" v-html="errors[0]"
                                                ></b-form-invalid-feedback>
                                            </b-form-group>
                                        </ValidationProvider>
                                    </div>
                                    <div class="col-12">
                                        <b-form-group :label="$t('parent_id')" :key="unitsKey">
                                            <units-selectbox 
                                                v-model="form.parent_id" :multiple="false"
                                            ></units-selectbox>
                                        </b-form-group>
                                    </div>
                                </div>
                            </ValidationObserver>
                        </div>
                        <div class="col-12 mt-3 d-flex">
                            <b-button @click="store" type="button" variant="primary" class="btn-lg mr-2">
                                {{ $t('save').toUpper() }}
                            </b-button>
                        </div>
                    </div>
                </template>
            </CommonModal>
        </app-layout>
    </div>
</template>

<script>
import AppLayout from '@/layouts/AppLayout'
import Header from '@/layouts/AppLayout/Header'
import HeaderMobile from '@/layouts/AppLayout/HeaderMobile'
import DatatableFilter from '@/components/datatable/DatatableFilter.vue'
import Datatable from '@/components/datatable/Datatable.vue'
import qs from 'qs'
import UnitService from '@/services/UnitService'
import CommonModal from '@/components/elements/CommonModal.vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import UnitsSelectbox from '../../../components/interactive-fields/UnitsSelectbox.vue'

export default {
    components: {
        AppLayout,
        Header,
        HeaderMobile,
        DatatableFilter,
        Datatable,
        CommonModal,
        ValidationProvider,
        ValidationObserver,
        UnitsSelectbox,
    },
    metaInfo() {
        return {
            title: this.$t('unit')
        }
    },
    data() {
        return {
            id: 0,
            form: {},
            unitsKey: 0,
            datatable: {
                isLoading: false,
                columns: [
                    {
                        label: '',
                        field: 'buttons',
                        html: true,
                        sortable: false,
                        tdClass: 'p-0 text-center w-40 align-middle',
                        buttons: [
                            {
                                text: this.$t('edit'),
                                class: 'ri-edit-box-line',
                                permission: 'unit_update',
                                callback: (row) => {
                                    this.id = row.id
                                    this.loadData()
                                }
                            },
                            {
                                text: this.$t('delete'),
                                class: 'ri-delete-bin-2-line',
                                permission: 'unit_delete',
                                callback: (row) => {
                                    this.delete(row.id)
                                }
                            },
                        ]
                    },
                    {
                        label: this.$t('id'),
                        field: 'id',
                        sortable: false,
                    },
                    {
                        label: this.$t('name'),
                        field: 'name',
                        sortable: true,
                    },
                    {
                        label: this.$t('name_en'),
                        field: 'name_en',
                        sortable: false,
                    }
                ],
                rows: [],
                total: 0,
                filterStatus: true,
                showTable: false,
                queryParams: {
                    filter: {},
                    sort: 'id',
                    page: 1,
                    limit: 20
                }
            }
        }
    },
    methods: {
        filter() {
            this.datatable.queryParams.page = 1
            this.getRows()
        },
        filterClear() {
            this.datatable.queryParams.filter = {}
            this.getRows()
        },
        onPageChange(params) {
            this.datatable.queryParams.page = params.currentPage
            this.getRows()
        },
        onPerPageChange(params) {
            this.datatable.queryParams.limit = params.currentPerPage
            this.datatable.queryParams.page = 1
            this.getRows()
        },
        onSortChange(params) {
            const sortType = params[0].type == 'desc' ? '-' : ''
            this.datatable.queryParams.sort = sortType + params[0].field
            this.getRows()
        },
        getRows() {
            this.datatable.showTable = true
            this.datatable.isLoading = true

            const config = {
                params: {
                    ...this.datatable.queryParams
                },
                paramsSerializer: (params) => qs.stringify(params, { encode: false })
            }

            return UnitService.getAll(config)
                .then((response) => {
                    this.datatable.rows = response.data.data
                    this.datatable.total = response.data.pagination.total
                })
                .finally(() => {
                    this.datatable.isLoading = false
                })
        },
        loadData() {
            if (this.id > 0) {
                UnitService.get(this.id)
                    .then(response => {
                        this.$refs.storeForm.reset()
                        this.form = response.data.data
                        this.$refs.modal.$refs.commonModal.show()
                    })
                    .catch(error => {
                        if (error.data.message) {
                            this.$toast.error(this.$t('api.' + error.data.message))
                        }
                    })
            }
        },
        async store() {
            const isValid = await this.$refs.storeForm.validate()

            if (isValid) {
                this.unitsKey = !this.unitsKey

                if (this.id == 0) {
                    UnitService.store(this.form, this.value)
                        .then((response) => {
                            this.$toast.success(this.$t('api.' + response.data.message))
                            this.$refs.modal.$refs.commonModal.hide()
                            this.filter()
                        })
                        .catch((error) => {
                            this.$toast.error(this.$t('api.' + error.data.message))
                        })
                }
                else {
                    UnitService.update(this.id, this.form)
                        .then((response) => {
                            this.$toast.success(this.$t('api.' + response.data.message))
                            this.$refs.modal.$refs.commonModal.hide()
                            this.filter()
                        })
                        .catch((error) => {
                            this.$toast.error(this.$t('api.' + error.data.message))
                        })
                }
            }
        },
        delete(id) {
            this.deleteModal(() => {
                this.unitsKey = !this.unitsKey
                
                UnitService.deleteUnit(id)
                    .then(response => {
                        this.$toast.success(this.$t('api.' + response.data.message))
                        this.filter()
                    })
                    .catch(error => {
                        this.$toast.error(this.$t('api.' + error.data.message))
                    })
            })
        },
        add() {
            this.id = 0
            this.$refs.storeForm.reset()
            this.form = {}
            this.$refs.modal.$refs.commonModal.show()
        }
    }
}
</script>

